import React, { useState, useCallback, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Channel, PopulatedChannel } from "../../types";
import { FRONTEND_BASE_URL, ROUTES, PLATFORMS } from "../../lib/constants";
import Button from "../../components/ui/Button";
import Badge from "../../components/ui/Badge";
import toast from "react-hot-toast";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCopy as faCopyIcon,
  faPlus,
  faTrash,
  faEye,
  faList,
  faUsers,
  faEdit,
} from "@fortawesome/free-solid-svg-icons";
import { faTelegram, faDiscord, faSlack } from "@fortawesome/free-brands-svg-icons";
import {
  addReferralCode,
  deleteReferralCode,
  getChannelDetails,
} from "../../lib/apiClient";
import { getErrorMessage, getChannelPlatform } from "../../lib/utils";

interface ChannelListItemProps {
  channel: PopulatedChannel | Channel;
}

const ChannelListItem: React.FC<ChannelListItemProps> = ({ channel }) => {
  const isActive = channel.is_active ?? false;
  const platform = getChannelPlatform(channel);
  const isTelegram = platform === PLATFORMS.TELEGRAM;
  const isDiscord = platform === PLATFORMS.DISCORD;
  const isSlack = platform === PLATFORMS.SLACK;

  const [isCopying, setIsCopying] = useState(false);
  const navigate = useNavigate();
  const [isGenerating, setIsGenerating] = useState(false);
  const [isDeleting, setIsDeleting] = useState<string | null>(null);
  const [referralCodes, setReferralCodes] = useState<string[]>([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [copiedCode, setCopiedCode] = useState<string | null>(null);

  const dropdownRef = useRef<HTMLDivElement>(null);

  // Sync referral codes from channel
  useEffect(() => {
    const codes = Array.isArray((channel as any).referralCode)
      ? ((channel as any).referralCode as string[])
      : (channel as any).referralCode
        ? [String((channel as any).referralCode)]
        : [];
    setReferralCodes(codes);
  }, [(channel as any).referralCode]);

  // Close dropdown if clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const loadReferralCodes = useCallback(async () => {
    try {
      const fresh = await getChannelDetails(channel._id);
      const codes = Array.isArray((fresh as any).referralCode)
        ? ((fresh as any).referralCode as string[])
        : (fresh as any).referralCode
          ? [String((fresh as any).referralCode)]
          : [];
      setReferralCodes(codes);
    } catch (err) {
      toast.error(getErrorMessage(err) || "Failed to load referral codes");
    }
  }, [channel._id]);

  const handleGenerateReferralCode = async () => {
    setIsGenerating(true);
    try {
      const code = await addReferralCode(channel._id);
      toast.success("Referral code generated!");
      setReferralCodes((prev) => [...prev, code]);

    } catch (err) {
      toast.error(getErrorMessage(err) || "Failed to generate referral code");
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDeleteReferralCode = async (code: string) => {
    setIsDeleting(code);
    try {
      await deleteReferralCode(channel._id, code);
      setReferralCodes((prev) => prev.filter((c) => c !== code));
      toast.success("Referral code deleted");
    } catch (err) {
      toast.error("Failed to delete referral code");
    } finally {
      setIsDeleting(null);
    }
  };

  const handleCopyLink = async (code: string) => {
    const link = `${FRONTEND_BASE_URL}${ROUTES.PUBLIC_CHANNEL_PLANS(code)}`;
    setIsCopying(true);
    try {
      await navigator.clipboard.writeText(link);
      setCopiedCode(code);
      setTimeout(() => setCopiedCode(null), 1000);
      toast.success("Channel link copied!");
    } catch (err) {
      toast.error("Failed to copy link");
    } finally {
      setTimeout(() => setIsCopying(false), 500);
    }
  };

  return (
    <div className="bg-dark-primary p-4 rounded-lg shadow hover:shadow-md transition-all border border-dark-tertiary hover:border-golden-accent flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-3 sm:space-y-0">
      {/* Channel Info */}
      <div className="flex-grow mr-4 overflow-hidden">
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          {/* Platform Badge */}
          <div
            className={`flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${
              isDiscord
                ? "bg-[#5865F2]/20 text-[#5865F2]"
                : isSlack
                ? "bg-[#4A154B]/20 text-[#4A154B]"
                : "bg-[#0088cc]/20 text-[#0088cc]"
            }`}
          >
            <FontAwesomeIcon
              icon={isDiscord ? faDiscord : isSlack ? faSlack : faTelegram}
              className="text-sm"
            />
            {isDiscord ? "Discord" : isSlack ? "Slack" : "Telegram"}
          </div>
          <h3
            className="text-lg font-semibold text-text-primary truncate"
            title={channel.name}
          >
            {channel.name}
          </h3>
          <Badge status={isActive ? "active" : "inactive"} size="sm" />
        </div>
        <p className="text-sm text-text-secondary mt-0.5 truncate">
          {isTelegram ? (
            <>
              <span className="font-medium">Chat ID:</span> {channel.telegram_chat_id || "N/A"}
            </>
          ) : isSlack ? (
            <>
              <span className="font-medium">Channel:</span> {channel.slack_channel_id || "N/A"}
              {channel.slack_team_id && (
                <span className="ml-3">
                  <span className="font-medium">Team:</span> {channel.slack_team_id}
                </span>
              )}
            </>
          ) : (
            <>
              <span className="font-medium">Guild:</span> {channel.discord_guild_id || "N/A"}
              {channel.discord_role_id && (
                <span className="ml-3">
                  <span className="font-medium">Role:</span> {channel.discord_role_id}
                </span>
              )}
            </>
          )}
        </p>
        {channel.description && (
          <p
            className="text-xs text-text-secondary mt-0.5 truncate"
            title={channel.description}
          >
            {channel.description}
          </p>
        )}
        {Array.isArray(channel.associated_plan_ids) && (
          <p className="text-xs text-text-secondary mt-0.5">
            {channel.associated_plan_ids.length} Plan(s) Associated
          </p>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex flex-shrink-0 flex-wrap gap-2 justify-end">
        {/* Public Dropdown */}
        {isActive && (
          <div className="relative" ref={dropdownRef}>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setDropdownOpen((o) => !o)}
            >
              <span className="flex items-center gap-1">
                Public
                <FontAwesomeIcon icon={faList} />
              </span>
            </Button>

            {dropdownOpen && (
              <div className="absolute left-0 mt-1 w-full max-w-[95vw] min-w-[180px] sm:left-1/2 sm:-translate-x-1/2 md:left-auto md:right-0 md:translate-x-0 md:w-64 md:max-w-xs bg-dark-secondary border border-dark-tertiary rounded-lg shadow-lg z-50 p-2 sm:p-0 flex flex-col gap-2 sm:gap-0"
              >
                {referralCodes.length === 0 && (
                  <div className="px-3 py-2 text-sm text-gray-400">
                    No referral codes yet.
                  </div>
                )}

                {referralCodes.map((code) => (
                  <div
                    key={code}
                    className="flex flex-col sm:flex-row sm:items-center justify-between px-3 py-2 hover:bg-dark-tertiary gap-2 sm:gap-0"
                  >
                    <span className="font-mono text-lg truncate break-all max-w-full sm:max-w-[8rem]">{code}</span>
                    <div className="flex items-center gap-2 self-end sm:self-auto">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => handleCopyLink(code)}
                        isLoading={isCopying}
                        disabled={isCopying}
                        title="Copy Public Combo Link"
                      >
                        <FontAwesomeIcon icon={faCopyIcon} />
                      </Button>
                      <Button
                        variant="danger"
                        size="sm"
                        title="Delete"
                        onClick={() => handleDeleteReferralCode(code)}
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </Button>
                    </div>
                  </div>
                ))}

                {/* Add new referral code */}
                <div
                  className="flex items-center justify-center px-3 py-2 text-green-400 hover:text-green-600 hover:bg-dark-tertiary cursor-pointer"
                  onClick={handleGenerateReferralCode}
                >
                  <FontAwesomeIcon icon={faPlus} className="mr-1" /> Add New Link
                </div>
              </div>
            )}
          </div>
        )}

        {/* Other Navigation Buttons */}
        <Link to={ROUTES.ADMIN_CHANNEL_OVERVIEW(channel._id)}>
          <Button variant="secondary" size="sm">
            <FontAwesomeIcon icon={faEye} className="mr-1" />
            Overview
          </Button>
        </Link>
        <Link to={ROUTES.ADMIN_CHANNEL_PLANS(channel._id)}>
          <Button variant="secondary" size="sm">
            <FontAwesomeIcon icon={faList} className="mr-1" />
            Plans
          </Button>
        </Link>
        <Link to={ROUTES.ADMIN_CHANNEL_SUBS(channel._id)}>
          <Button variant="secondary" size="sm">
            <FontAwesomeIcon icon={faUsers} className="mr-1" />
            Subs
          </Button>
        </Link>
        <Link to={ROUTES.ADMIN_CHANNEL_EDIT(channel._id)}>
          <Button variant="info" size="sm">
            <FontAwesomeIcon icon={faEdit} className="mr-1" />
            Edit
          </Button>
        </Link>
      </div>
    </div>
  );
};

export default ChannelListItem;
